import { flattenDeep, get } from 'lodash';
import {
  IconAlignJustified,
  IconCrystalBall,
  IconDatabaseSearch,
  IconLayoutGrid,
  IconMathFunction,
  TablerIcon,
} from '@rossum/ui/icons/tabler';
import {
  AnyDatapointSchema,
  OriginalDatapointSchema,
  SchemaUIConfiguration,
  UiFieldType,
} from '../../../types/schema';
import { SchemaBeforeFlattening, SchemaFulfilledPayload } from './types';

export const flattenSchema = (
  schema: SchemaBeforeFlattening
): SchemaFulfilledPayload => ({
  ...schema,
  content: flattenDeep(
    schema.content.map((tree, index) => flattenSchemaTree(tree, [`${index}`]))
  ),
});

// using lodash's `get` here to avoid the necessity of having typeguard
// TS cannot infer correctly the type from AnyDatapointSchema union
export const getUIConfigurationFromSchema = (
  schema: AnyDatapointSchema | undefined
) => {
  const uiConfiguration: SchemaUIConfiguration | undefined = get(schema, [
    'uiConfiguration',
  ]);

  return uiConfiguration;
};

export const getUIEditabilityFromSchema = (
  schema: AnyDatapointSchema | undefined
) => {
  const uiEdit = getUIConfigurationFromSchema(schema)?.edit ?? 'enabled';

  return uiEdit;
};

export const getUITypeFromSchema = (schema: AnyDatapointSchema | undefined) => {
  const uiType = getUIConfigurationFromSchema(schema)?.type;

  return uiType;
};

export const isFieldEditable = (schema: AnyDatapointSchema | undefined) => {
  const uiEdit = getUIEditabilityFromSchema(schema);

  return uiEdit !== 'disabled';
};

const unselectableTypesWithoutBbox: Array<NonNullable<UiFieldType>> = [
  'data',
  'manual',
  'formula',
  'reasoning',
  'lookup',
];

const isUnselectableType = (schema: AnyDatapointSchema | undefined) => {
  const uiFieldType = getUITypeFromSchema(schema);

  return uiFieldType && unselectableTypesWithoutBbox.includes(uiFieldType);
};

/** Denotes if datapoint is focusable/selectable based on its schema configuration
 *
 * Exceptional states are when uiConfiguration.type is not defined (undefined or null - legacy fields) or set to `captured`
 * in this case it is considered selectable even with edit: `disabled` because user can enter new value using bbox. `captured` fields
 * have special placeholder "Use a bounding box" in this case.
 *
 */
export const isFieldSelectable = (schema: AnyDatapointSchema | undefined) => {
  const dependsOnEditability = isUnselectableType(schema);
  const isEditable = isFieldEditable(schema);

  return dependsOnEditability ? isEditable : true;
};

const canHaveBbox = (schema: AnyDatapointSchema | undefined) => {
  return !!(
    schema &&
    schema.category === 'datapoint' &&
    !['enum', 'button'].includes(schema.type)
  );
};

export const isBboxVisible = (schema: AnyDatapointSchema | undefined) => {
  return !!(canHaveBbox(schema) && !isUnselectableType(schema));
};

export const canDrawBbox = (schema: AnyDatapointSchema | undefined) => {
  return canHaveBbox(schema) && isFieldSelectable(schema);
};

// TODO: @formulas-v2 - unify place of icons definition with fieldsIconsMap
export const uiFieldTypeIconsMap: Partial<
  Record<NonNullable<UiFieldType>, TablerIcon>
> = {
  data: IconLayoutGrid,
  manual: IconAlignJustified,
  formula: IconMathFunction,
  reasoning: IconCrystalBall,
  lookup: IconDatabaseSearch,
};

const flattenSchemaTree = (
  tree: OriginalDatapointSchema,
  path: Array<string>
): AnyDatapointSchema[] | AnyDatapointSchema => {
  if ('children' in tree && tree.children !== undefined) {
    const childrenArray = ([] as OriginalDatapointSchema[]).concat(
      tree.children
    );
    return [
      { ...tree, path, children: childrenArray.map(({ id }) => id) },
      ...childrenArray
        .map((child: OriginalDatapointSchema, index) => {
          const indexSuffix = Array.isArray(tree.children) ? [`${index}`] : [];
          const newPath = [...path, 'children', ...indexSuffix];
          return flattenSchemaTree(child, newPath);
        })
        .flat(),
    ];
  }

  return { ...tree, path, children: undefined };
};
